import { expandRoleAliases, normalizeRoleKey } from "./roleHelpers.mjs";

export const MITP_JOB_ACTIVE_STATUSES = Object.freeze(["queued", "running"]);
export const MITP_RESULT_STATUSES = Object.freeze([
  "updated",
  "skipped_existing",
  "unchanged",
  "unmatched",
  "error",
]);
export const MITP_CANDIDATE_STATUSES = Object.freeze([
  "eligible",
  "needs_review",
  "created",
  "linked_existing",
  "skipped_inactive",
  "skipped_closed",
]);
export const MITP_CANDIDATE_FILTERS = Object.freeze(["open", "eligible", "needs_review", "created", "linked_existing", "skipped_inactive", "skipped_closed", "all"]);
export const MITP_CANDIDATE_BOARD_STATUSES = Object.freeze(["not_added", "added", "already_in_board", "error"]);

export const readStoredRoleKeys = () => {
  if (typeof window === "undefined" || !window.localStorage) return [];
  const keys = [];
  const pushRole = (value) => {
    const normalized = normalizeRoleKey(value);
    if (normalized) keys.push(normalized);
  };

  pushRole(window.localStorage.getItem("user-role"));
  pushRole(window.localStorage.getItem("user-primary-role"));

  try {
    const rawRoles = JSON.parse(window.localStorage.getItem("user-roles") || "[]");
    if (Array.isArray(rawRoles)) {
      rawRoles.forEach((entry) => {
        if (typeof entry === "string") {
          pushRole(entry);
          return;
        }
        pushRole(entry?.key || entry?.role || entry?.name);
      });
    }
  } catch (_) {
    return [...new Set(keys)];
  }

  return [...new Set(keys)];
};

export const buildRoleAliasSet = (roleKeys = []) => {
  const roleSet = new Set();
  (Array.isArray(roleKeys) ? roleKeys : []).forEach((roleKey) => {
    const normalized = normalizeRoleKey(roleKey);
    if (!normalized) return;
    roleSet.add(normalized);
    expandRoleAliases(normalized).forEach((alias) => roleSet.add(alias));
  });
  return roleSet;
};

export const buildLiteralRoleSet = (roleKeys = []) => {
  const roleSet = new Set();
  (Array.isArray(roleKeys) ? roleKeys : []).forEach((roleKey) => {
    const normalized = normalizeRoleKey(roleKey);
    if (normalized) roleSet.add(normalized);
  });
  return roleSet;
};

export const canAccessMitpAppSyncs = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miridar");
};

export const canAccessExternalApiWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin");
};

export const canAccessMailjetWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin") || roleSet.has("privacy-manager");
};

export const canManageMailjetWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin");
};

export const canAccessCompanySmtpWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin");
};

export const canAccessPbxWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin");
};

export const canAccessTelegramWorkspace = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildLiteralRoleSet(roleKeys);
  return roleSet.has("admin") || roleSet.has("miniadmin");
};

export const canAccessDataIntegrationsImports = (roleKeys = readStoredRoleKeys()) => {
  const roleSet = buildRoleAliasSet(roleKeys);
  return (
    roleSet.has("admin") ||
    roleSet.has("miniadmin") ||
    roleSet.has("crm-admin") ||
    roleSet.has("crm-operator")
  );
};

export const normalizeMitpStatus = (value) => (value ? String(value).trim().toLowerCase() : "");

export const isMitpJobActive = (status) => MITP_JOB_ACTIVE_STATUSES.includes(normalizeMitpStatus(status));

export const resolveMitpStatusMeta = (status) => {
  const normalized = normalizeMitpStatus(status);
  if (normalized === "completed") return { color: "success", variant: "tonal" };
  if (normalized === "failed") return { color: "error", variant: "tonal" };
  if (normalized === "running") return { color: "warning", variant: "tonal" };
  if (normalized === "queued") return { color: "neutral", variant: "tonal" };
  return { color: "neutral", variant: "tonal" };
};

export const resolveMitpResultStatusMeta = (status) => {
  const normalized = normalizeMitpStatus(status);
  if (normalized === "updated") return { color: "success", variant: "tonal" };
  if (normalized === "skipped_existing") return { color: "warning", variant: "tonal" };
  if (normalized === "unchanged") return { color: "neutral", variant: "tonal" };
  if (normalized === "unmatched") return { color: "info", variant: "tonal" };
  if (normalized === "error") return { color: "error", variant: "tonal" };
  return { color: "neutral", variant: "tonal" };
};

export const resolveMitpCandidateStatusMeta = (status) => {
  const normalized = normalizeMitpStatus(status);
  if (normalized === "eligible") return { color: "tertiary", variant: "tonal" };
  if (normalized === "needs_review") return { color: "secondary", variant: "tonal" };
  if (normalized === "created") return { color: "success", variant: "tonal" };
  if (normalized === "linked_existing") return { color: "info", variant: "tonal" };
  if (normalized === "skipped_closed" || normalized === "skipped_inactive") {
    return { color: "neutral", variant: "tonal" };
  }
  return { color: "neutral", variant: "tonal" };
};

export const resolveMitpCandidateBoardStatusMeta = (status) => {
  const normalized = normalizeMitpStatus(status);
  if (normalized === "added") return { color: "success", variant: "tonal" };
  if (normalized === "already_in_board") return { color: "info", variant: "tonal" };
  if (normalized === "error") return { color: "error", variant: "tonal" };
  return { color: "neutral", variant: "tonal" };
};

export const formatMitpStatusLabel = (status) =>
  String(status || "-")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const resolveMitpProgressPercent = (job) => {
  const total = Number(job?.registry_total || 0);
  const processed = Number(job?.processed_count || 0);
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((processed / total) * 100)));
};

export const normalizeMitpCompanySearchQuery = (value) =>
  String(value || "")
    .replace(/["'`]/g, " ")
    .replace(/\bS\.?\s*C\.?\b/gi, " ")
    .replace(/\bS\.?\s*R\.?\s*L\.?\b/gi, " ")
    .replace(/[.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeCandidateName = (value) =>
  normalizeMitpCompanySearchQuery(value)
    .replace(/\bL\.?\s*T\.?\s*D\.?\b/gi, " ")
    .replace(/\bL\.?\s*L\.?\s*C\.?\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const scoreMitpCompanyCandidate = (mitpCompanyName, candidate = {}) => {
  const mitpName = normalizeCandidateName(mitpCompanyName);
  const candidateName = normalizeCandidateName(candidate?.name);
  if (!mitpName || !candidateName) return 0;

  let score = 0;
  if (candidateName === mitpName) score += 100;
  if (candidateName.startsWith(mitpName) || mitpName.startsWith(candidateName)) score += 40;
  if (candidateName.includes(mitpName) || mitpName.includes(candidateName)) score += 20;
  if (String(candidate?.fiscal_code || "").trim()) score += 5;
  return score;
};

export const sortMitpCompanyCandidates = (mitpCompanyName, candidates = []) =>
  [...(Array.isArray(candidates) ? candidates : [])].sort((left, right) => {
    const scoreDelta = scoreMitpCompanyCandidate(mitpCompanyName, right) - scoreMitpCompanyCandidate(mitpCompanyName, left);
    if (scoreDelta !== 0) return scoreDelta;
    return String(left?.name || "").localeCompare(String(right?.name || ""));
  });
